"use client";

import { useEffect, useState } from "react";
import { FiGift } from "react-icons/fi";
import { useTypewriter } from "./useTypewriter";
import "./ChatbotSplash.scss";

const HINT_PHRASES = ["eligible benefits", "bookings"];

type ChatbotSplashProps = {
  /** True once the iframe has fired `onLoad`. */
  loaded: boolean;
  /**
   * Changes whenever the underlying iframe remounts (e.g. auth token or
   * deep-link nonce). Resets the min/max timers so the splash shows again.
   */
  resetKey: string;
  /** Minimum time the splash stays up so it doesn't flash on a fast load. */
  minMs?: number;
  /** Hard ceiling so a frame that never fires `onLoad` can't strand the splash. */
  maxMs?: number;
};

/**
 * Branded loading overlay shown over the chatbot iframe while it boots. Visible
 * until the frame loads (respecting a minimum window), or until `maxMs` elapses
 * as a safety net. Kept mounted and faded via a class so the reveal is smooth.
 */
export default function ChatbotSplash({
  loaded,
  resetKey,
  minMs = 2000,
  maxMs = 12000,
}: ChatbotSplashProps) {
  const [minElapsed, setMinElapsed] = useState(false);
  const [maxElapsed, setMaxElapsed] = useState(false);

  useEffect(() => {
    setMinElapsed(false);
    setMaxElapsed(false);
    const minT = setTimeout(() => setMinElapsed(true), minMs);
    const maxT = setTimeout(() => setMaxElapsed(true), maxMs);
    return () => {
      clearTimeout(minT);
      clearTimeout(maxT);
    };
  }, [resetKey, minMs, maxMs]);

  const visible = !maxElapsed && (!loaded || !minElapsed);
  const typed = useTypewriter(HINT_PHRASES, { active: visible });

  return (
    <div
      className={`aina-splash ${visible ? "" : "aina-splash--hidden"}`}
      aria-hidden={!visible}
    >
      <div className="aina-splash__logo">
        <span className="aina-splash__ring" />
        <span className="aina-splash__badge">
          <FiGift />
        </span>
      </div>
      <h3 className="aina-splash__title">AINA</h3>
      <p className="aina-splash__subtitle">Your benefits concierge</p>
      <p className="aina-splash__hint">
        Loading your
        <span className="aina-splash__typed">
          {typed}
          <span className="aina-splash__caret" />
        </span>
      </p>
      <div className="aina-splash__dots">
        <span />
        <span />
        <span />
      </div>
    </div>
  );
}
